/**
 * Domain types for the workspace, together with the decoder for each one.
 *
 * Types and decoders live side by side on purpose: if the API contract changes,
 * the decoder fails loudly at the boundary instead of letting a wrong shape reach
 * a component. Field names mirror the documented API exactly.
 */

import {
  DecodeError,
  arrayOf,
  bool,
  int,
  nullableInt,
  itemsEnvelope,
  literal,
  nullableStr,
  object,
  optional,
  str,
  type Decoder,
} from './decode';

/* ------------------------------------------------------------------ auth --- */

export interface SessionUser {
  readonly id: string;
  readonly email: string;
  readonly display_name: string;
  readonly is_platform_operator?: boolean;
  readonly status?: string;
  readonly must_change_password?: boolean;
}

/**
 * The login response, exactly as agreed:
 *   { user: { id, email, display_name }, csrf_token }
 *
 * The token is read from the body only. An earlier revision also accepted it from
 * the `X-CSRF-Token` response header; that was a compatibility shim for a backend
 * variant that is no longer part of the contract, and keeping it meant a session
 * could be considered signed in while holding a token from a source nobody had
 * agreed on. A response without a usable body token is now rejected outright,
 * because every later write would otherwise fail while the UI claimed success.
 */
export interface LoginResult {
  readonly csrf_token: string;
  readonly user: SessionUser;
}

export const decodeSessionUser: Decoder<SessionUser> = object({
  id: str,
  email: str,
  display_name: str,
  is_platform_operator: optional(bool, false),
  status: optional(str, 'active'),
  must_change_password: optional(bool, false),
});

export const decodeLoginResult: Decoder<LoginResult> = (value, path) => {
  if (typeof value !== 'object' || value === null) {
    throw new DecodeError(path, '登录响应对象');
  }
  const record = value as Record<string, unknown>;

  const user = decodeSessionUser(record['user'], `${path}.user`);

  const bodyToken = record['csrf_token'];
  if (typeof bodyToken !== 'string' || bodyToken === '') {
    throw new DecodeError(`${path}.csrf_token`, '非空字符串（缺少 CSRF 令牌时变更请求将失败）');
  }

  return { csrf_token: bodyToken, user };
};

/**
 * A sign-out the API actually performed.
 *
 * `revoked` distinguishes "the server invalidated the session" from "the browser
 * forgot it locally". The two must never be presented alike: only the first ends
 * the session for anyone holding the cookie.
 */
export interface LogoutResult {
  readonly revoked: boolean;
}

/**
 * `POST auth/logout` answers `204 No Content`; the transport decodes an empty body
 * to `null`. Either way the server confirmed the revocation, so this decoder only
 * has to reject a payload that claims something else.
 */
export const decodeLogoutResult: Decoder<LogoutResult> = (value, path) => {
  if (value === null) return { revoked: true };
  if (typeof value === 'string' && value.trim() === '') return { revoked: true };
  if (typeof value === 'object' && !Array.isArray(value)) return { revoked: true };
  throw new DecodeError(path, '204 空响应或对象');
};

/* --------------------------------------------------------------- tenants --- */

/**
 * Roles are an open set: the contract only says `role` is a string, and the server
 * column is a plain string. Typing it as a closed union would either reject valid
 * future roles (hiding every tenant from the user) or force a guess. The raw value
 * is kept, and `roleLabel` maps the known ones to Chinese while showing anything
 * unrecognised verbatim rather than mislabeling the user's permissions.
 */
export type TenantRole = string;

export interface Tenant {
  readonly id: string;
  readonly name: string;
  readonly role: TenantRole;
}

/**
 * `GET /tenants` -> `{ items: [{ id, name, role }] }`.
 *
 * Decoded exactly, with no compatibility fallback to the identity router's
 * `/auth/tenants` path (kept server-side for other callers): a second path meant a
 * 404 on the agreed endpoint could be reported as a working tenant list, hiding a
 * real contract break on the one call that gates the whole workspace.
 */
export const decodeTenant: Decoder<Tenant> = object({
  id: str,
  name: str,
  role: str,
});

export const decodeTenantList = itemsEnvelope(decodeTenant);

export const ROLE_LABELS: Readonly<Record<string, string>> = {
  owner: '所有者',
  admin: '管理员',
  analyst: '分析师',
  viewer: '只读成员',
};

/** Falls back to the raw role so an unknown value is never shown as a wrong label. */
export function roleLabel(role: TenantRole): string {
  return ROLE_LABELS[role] ?? role;
}

/* ---------------------------------------------------------------- topics --- */

export const TOPIC_STATUSES = ['active', 'paused', 'draft', 'archived'] as const;
export type TopicStatus = (typeof TOPIC_STATUSES)[number];

/** Who a topic is about; used by relevance analysis, not by keyword matching. */
export interface SubjectProfile {
  readonly subject: string;
  readonly aliases: readonly string[];
  readonly related: readonly string[];
  readonly confusables: readonly string[];
  readonly scope: string;
  readonly description: string;
}

export const EMPTY_PROFILE: SubjectProfile = {
  subject: '',
  aliases: [],
  related: [],
  confusables: [],
  scope: '',
  description: '',
};

export const decodeSubjectProfile: Decoder<SubjectProfile> = object({
  subject: optional(str, ''),
  aliases: optional(arrayOf(str), []),
  related: optional(arrayOf(str), []),
  confusables: optional(arrayOf(str), []),
  scope: optional(str, ''),
  description: optional(str, ''),
});

export interface Topic {
  readonly id: string;
  readonly name: string;
  readonly status: TopicStatus;
  readonly created_at: string;
  readonly include_terms: readonly string[];
  readonly exclude_terms: readonly string[];
  readonly subject_profile: SubjectProfile;
}

export const decodeTopic: Decoder<Topic> = object({
  id: str,
  name: str,
  status: literal(...TOPIC_STATUSES),
  created_at: str,
  // Not part of the minimal documented payload; defaults keep older responses valid.
  include_terms: optional(arrayOf(str), []),
  exclude_terms: optional(arrayOf(str), []),
  subject_profile: optional(decodeSubjectProfile, EMPTY_PROFILE),
});

export const decodeTopicList = itemsEnvelope(decodeTopic);

export interface CreateTopicInput {
  readonly name: string;
  readonly include_terms: readonly string[];
  readonly exclude_terms: readonly string[];
  readonly subject_profile?: SubjectProfile;
}

export const TOPIC_STATUS_LABELS: Readonly<Record<TopicStatus, string>> = {
  active: '监测中',
  paused: '已暂停',
  draft: '草稿',
  archived: '已归档',
};

/* -------------------------------------------------------------- contents --- */

/**
 * One visible content item, described by its current revision.
 *
 * `id` is the **stable content identity** (the server's `content_id`): it survives
 * new revisions and tombstones, so it is what a client may persist for cache keys,
 * deep links and selection state. `revision_id` names the immutable revision the
 * displayed fields were read from, and changes when the content is updated.
 *
 * `title`, `body` and `published_at` are nullable on purpose — a deletion tombstone
 * or a body-less source record genuinely has no value for them. They are decoded as
 * `string | null` / `string | null` rather than defaulted to `''`, because an empty
 * string would be indistinguishable from a truly empty title and would let the UI
 * render "（无标题）" for a record that simply has no title field at all.
 */
export interface ContentItem {
  readonly id: string;
  readonly revision_id: string;
  readonly content_id: string;
  readonly title: string | null;
  readonly body: string | null;
  readonly published_at: string | null;
  readonly source_name: string;
  readonly source_version: number;
  readonly data_scope?: string;
  readonly original_url?: string | null;
  readonly fulltext_status?: string | null;
  readonly source_summary?: string | null;
  readonly page_collection_status?: string | null;
  readonly page_error_code?: string | null;
}

export const decodeContentItem: Decoder<ContentItem> = object({
  id: str,
  revision_id: str,
  content_id: str,
  title: nullableStr,
  body: nullableStr,
  published_at: nullableStr,
  source_name: str,
  source_version: int,
  data_scope: optional(str, 'private'),
  original_url: optional(nullableStr, null),
  fulltext_status: optional(nullableStr, null),
  source_summary: optional(nullableStr, null),
  page_collection_status: optional(nullableStr, null),
  page_error_code: optional(nullableStr, null),
});

export const decodeContentList = object({
  items: arrayOf(decodeContentItem),
  next_cursor: optional(nullableStr, null),
});

/** Display helper: a nullable title/body has exactly one rendering. */
export const CONTENT_NO_TITLE = '（无标题）';
export const CONTENT_NO_BODY = '（正文为空）';
export const CONTENT_NO_PUBLISHED_AT = '未提供发布时间';

/* ---------------------------------------------------------------- alerts --- */

export const ALERT_SEVERITIES = ['low', 'medium', 'high', 'critical'] as const;
export type AlertSeverity = (typeof ALERT_SEVERITIES)[number];

export const ALERT_STATUSES = ['open', 'acknowledged', 'handling', 'closed', 'ignored'] as const;
export type AlertStatus = (typeof ALERT_STATUSES)[number];

export interface AlertItem {
  readonly id: string;
  readonly severity: AlertSeverity;
  readonly status: AlertStatus;
  readonly summary: string;
  readonly created_at: string;
  readonly topic_name: string | null;
  readonly task_count: number;
  readonly unread: boolean;
  /** `null` when the server answered with the prepared workspace shape. */
  readonly topic_id: string | null;
  /** Optimistic-lock version a transition must send; `null` when not provided. */
  readonly version: number | null;
}

/**
 * Alert rules store `info | warning | critical`; the workspace vocabulary is
 * `low | medium | high | critical`. `high` has no rule-level equivalent.
 */
const RULE_TO_SEVERITY: Readonly<Record<string, AlertSeverity>> = {
  info: 'low',
  warning: 'medium',
  critical: 'critical',
};
const SEVERITY_TO_RULE: Readonly<Partial<Record<AlertSeverity, string>>> = {
  low: 'info',
  medium: 'warning',
  critical: 'critical',
};

/** Workspace severity → the value `/alerts/instances?severity=` filters on. */
export function severityToRule(severity: AlertSeverity): string {
  return SEVERITY_TO_RULE[severity] ?? severity;
}

/** Accepts either vocabulary and returns the workspace one. */
export const decodeSeverity: Decoder<AlertSeverity> = (value, path) => {
  const mapped = typeof value === 'string' ? RULE_TO_SEVERITY[value] : undefined;
  return mapped ?? literal(...ALERT_SEVERITIES)(value, path);
};

const METRIC_LABELS: Readonly<Record<string, string>> = {
  content_count: '内容数',
  negative_ratio: '负面占比',
  negative_count: '负面内容数',
  unanalyzed_ratio: '未分析占比',
  distinct_content_count: '去重内容数',
};
const COMPARATORS: Readonly<Record<string, string>> = { gt: '>', gte: '>=', lt: '<', lte: '<=' };

/**
 * `/alerts/instances` rows carry the rule figures instead of a prepared summary;
 * build the same sentence the workspace endpoint uses.
 *
 * An anomaly alert (one with a `score`) stores a robust z as its threshold, so
 * "value ≥ 阈值 z" would compare unlike quantities; it reads as the value and score.
 */
function instanceSummary(value: Record<string, unknown>): string | undefined {
  const { metric, observed_value: observed, comparator, threshold, score } = value;
  if (typeof metric !== 'string') return undefined;
  const label = METRIC_LABELS[metric] ?? metric;
  if (typeof score === 'number') {
    return `${label} ${String(observed)} 异常（评分 ${String(score)}/10）`;
  }
  const op = typeof comparator === 'string' ? (COMPARATORS[comparator] ?? comparator) : '';
  return `${label} ${String(observed)} ${op} 阈值 ${String(threshold)}`;
}

const decodeAlertShape = object({
  id: str,
  severity: decodeSeverity,
  status: literal(...ALERT_STATUSES),
  summary: str,
  created_at: str,
  topic_name: optional(nullableStr, null),
  task_count: optional(int, 0),
  unread: optional(bool, false),
  topic_id: optional(nullableStr, null),
  version: optional(nullableInt, null),
});

export const decodeAlertItem: Decoder<AlertItem> = (value, path) => {
  if (value !== null && typeof value === 'object' && !('summary' in value)) {
    const row = value as Record<string, unknown>;
    const createdAt = row['triggered_at'] ?? row['window_end'];
    return decodeAlertShape({ ...row, summary: instanceSummary(row), created_at: createdAt }, path);
  }
  return decodeAlertShape(value, path);
};

export const decodeAlertList = itemsEnvelope(decodeAlertItem);

export const SEVERITY_LABELS: Readonly<Record<AlertSeverity, string>> = {
  low: '低',
  medium: '中',
  high: '高',
  critical: '紧急',
};

export const ALERT_STATUS_LABELS: Readonly<Record<AlertStatus, string>> = {
  open: '待处理',
  acknowledged: '已确认',
  handling: '处理中',
  closed: '已关闭',
  ignored: '已忽略',
};

/* ------------------------------------------------------------------ jobs --- */

export const JOB_STATUSES = [
  'queued',
  'leased',
  'running',
  'completed',
  'failed',
  'dead_letter',
  'cancel_requested',
  'cancelled',
  'revoked',
] as const;
export type JobStatus = (typeof JOB_STATUSES)[number];

export interface JobItem {
  readonly id: string;
  readonly kind: string;
  readonly status: JobStatus;
  readonly created_at: string;
}

export const decodeJobItem: Decoder<JobItem> = object({
  id: str,
  kind: str,
  status: literal(...JOB_STATUSES),
  created_at: str,
});

export const decodeJobList = itemsEnvelope(decodeJobItem);

export const JOB_STATUS_LABELS: Readonly<Record<JobStatus, string>> = {
  queued: '排队中',
  leased: '已领取',
  running: '执行中',
  completed: '已完成',
  failed: '失败',
  dead_letter: '死信',
  cancel_requested: '请求取消',
  cancelled: '已取消',
  revoked: '授权已撤销',
};

/** Buckets used by the task-status summary strip. */
export function isJobSettled(status: JobStatus): boolean {
  return (
    status === 'completed' ||
    status === 'failed' ||
    status === 'dead_letter' ||
    status === 'cancelled' ||
    status === 'revoked'
  );
}

export function isJobActive(status: JobStatus): boolean {
  return status === 'queued' || status === 'leased' || status === 'running';
}

/* --------------------------------------------------------------- reports --- */

export const REPORT_STATUSES = ['draft', 'published', 'error'] as const;
export type ReportStatus = (typeof REPORT_STATUSES)[number];
export interface ReportVersion {
  readonly id: string;
  readonly version_number: number;
  readonly status: ReportStatus;
  readonly document_schema_version: string;
  readonly content_hash: string;
  readonly error_code: string | null;
  readonly error_detail: string | null;
  readonly created_at: string;
  readonly published_at: string | null;
}
export interface ReportItem {
  readonly id: string;
  readonly topic_id: string;
  readonly topic_name: string;
  readonly period_start: string;
  readonly period_end: string;
  readonly period_label: string | null;
  readonly attempt_count: number;
  readonly created_at: string;
  readonly snapshot: {
    readonly captured_at: string | null;
    readonly watermark_received_at: string | null;
    readonly topic_rule_version: number | null;
    readonly item_count: number;
    readonly snapshot_cap: number;
    readonly complete: boolean;
    readonly cap_exceeded: boolean;
    readonly manifest_fingerprint: string | null;
  };
  readonly versions: ReportVersion[];
  readonly invalidation_reason: string | null;
}
const decodeReportVersion: Decoder<ReportVersion> = object({
  id: str,
  version_number: int,
  status: literal(...REPORT_STATUSES),
  document_schema_version: str,
  content_hash: str,
  error_code: nullableStr,
  error_detail: nullableStr,
  created_at: str,
  published_at: nullableStr,
});
export const decodeReportItem: Decoder<ReportItem> = object({
  id: str,
  topic_id: str,
  topic_name: str,
  period_start: str,
  period_end: str,
  period_label: nullableStr,
  attempt_count: int,
  created_at: str,
  snapshot: object({
    captured_at: nullableStr,
    watermark_received_at: nullableStr,
    topic_rule_version: nullableInt,
    item_count: int,
    snapshot_cap: int,
    complete: bool,
    cap_exceeded: bool,
    manifest_fingerprint: nullableStr,
  }),
  versions: arrayOf(decodeReportVersion),
  invalidation_reason: nullableStr,
});
export const decodeReportList = itemsEnvelope(decodeReportItem);
export const REPORT_STATUS_LABELS: Readonly<Record<ReportStatus, string>> = {
  draft: '待审核',
  published: '已发布',
  error: '生成失败',
};
/* ---------------------------------------------------------------- health --- */

export interface HealthProbe {
  readonly reachable: boolean;
}

/**
 * Connectivity probe. The backend exposes no documented unauthenticated status
 * endpoint, so this only classifies whether the API service answered with a
 * parseable JSON body at all; it makes no claim about business readiness.
 */
export const decodeHealthProbe: Decoder<HealthProbe> = (value) => {
  if (typeof value === 'object' && value !== null) return { reachable: true };
  return { reachable: true };
};
